import { AttachmentChips, AttachmentDropOverlay } from '@viko/transcript-ui'
import { X } from 'lucide-react'
import type { ReactNode } from 'react'
import type { AttachmentInput } from '../lib/attachment-input.js'
import { formatSize } from '../lib/attachment-input.js'
import { Button } from './ui.js'

/** Rejection messages and the "attach large pasted text as a file" offer for an input. */
export function AttachmentNotices({ input }: { input: AttachmentInput }) {
  const { notices, largePaste } = input
  if (!notices.length && largePaste == null) return null
  return (
    <div className="grid gap-2 pt-2 text-[14px]">
      {notices.map((n) => (
        <p
          key={n.id}
          role="alert"
          className="m-0 flex items-start gap-2 rounded-lg bg-status-wait-soft px-3 py-2"
        >
          <span className="min-w-0 flex-1">{n.text}</span>
          <button
            type="button"
            className="grid h-7 w-7 flex-none place-items-center rounded-full"
            aria-label="Dismiss message"
            onClick={() => input.dismissNotice(n.id)}
          >
            <X size={14} aria-hidden="true" />
          </button>
        </p>
      ))}
      {largePaste != null ? (
        <div
          role="group"
          aria-label="Large paste"
          className="flex flex-wrap items-center gap-2 rounded-lg bg-surface-2 px-3 py-2"
        >
          <span className="flex-1">Pasted {formatSize(new Blob([largePaste]).size)} of text.</span>
          <Button size="sm" onClick={() => input.resolveLargePaste('file')}>
            Attach as file
          </Button>
          <Button size="sm" variant="ghost" onClick={() => input.resolveLargePaste('text')}>
            Paste as text
          </Button>
        </div>
      ) : null}
    </div>
  )
}

/**
 * Wraps any panel/input: drop files anywhere on it ("Drop to attach"), and shows the chips and
 * notices for `input` (from `useAttachmentInput`). Put the textarea's `onPaste={input.onPaste}`.
 */
export function AttachmentDropZone({
  input,
  children,
  className,
  chips = true,
}: {
  input: AttachmentInput
  children: ReactNode
  className?: string
  /** Render chips + notices under the children (turn off if the input renders its own). */
  chips?: boolean
}) {
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: drop target for files
    <div className={`relative ${className ?? ''}`} {...input.dropProps}>
      <AttachmentDropOverlay active={input.dragging} />
      {children}
      {chips ? (
        <>
          <AttachmentChips items={input.items} onRemove={input.remove} className="!px-0" />
          <AttachmentNotices input={input} />
        </>
      ) : null}
    </div>
  )
}
